import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Clock } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { useScreenShown, useSwapScroll } from '@papan/core-ui/ui'
import type { RecentWorkFilter, RecentWorkGroup, RecentWorkScope } from '@papan/files/client'
import { fileKeys, useRecentWork } from '@papan/files/client/queries'
import { RecentGroupList, type RecentNav } from './RecentGroupRow'
import { groupByDay, type RecentWords } from './recentText'

const FILTERS: readonly RecentWorkFilter[] = ['all', 'edited', 'uploaded', 'ai']
/** Pages read on their own while they come back EMPTY with more to read (everything a page scanned
 *  was hidden from the viewer); past this the person asks with Show more. */
const AUTO_CONTINUE_PAGES = 8
const PAGE_SIZE = 25

/** Where a Recent row leads — shared by the page, Home's "Jump back in" and the phone. */
export function useRecentNav(urlPrefix: string): RecentNav {
  const navigate = useNavigate()
  return useMemo(() => ({
    revealFile: (f) => navigate(f.parentId ? `${urlPrefix}/folder/${f.parentId}?reveal=${f.id}` : `${urlPrefix}/folder?reveal=${f.id}`),
    openPlace: (g: RecentWorkGroup) => {
      if (!g.place) return navigate(`${urlPrefix}/folder`)
      // As a project card opens: an app as its window, a project as its workspace.
      if (g.kind === 'app') return navigate(`${urlPrefix}/app/${g.place.id}`)
      if (g.kind === 'project') return navigate(`${urlPrefix}/workspace/${g.place.id}`)
      return navigate(`${urlPrefix}/folder/${g.place.id}`)
    },
  }), [navigate, urlPrefix])
}

/** The drive's own name for its top, as the Drive shows it. */
export function useRecentWords(orgEmail: string | null | undefined, tenantName: string | undefined): RecentWords {
  const { t } = useTranslation('files')
  return useMemo(() => ({
    t: (key: string, opts?: Record<string, unknown>) => t(key, opts) as string,
    viewerEmail: orgEmail,
    rootLabel: tenantName?.trim() ? t('driveView.title', { tenant: tenantName.trim() }) : t('driveHome.nav.title'),
  }), [t, orgEmail, tenantName])
}

/**
 * #653 — the Recent page (`/recent`): recent changes in the drive, grouped into pieces of work,
 * in day sections. Everyone's changes by default (owner, 2026-10-23); Mine and the kind-of-change
 * filters live in the URL, so back and refresh keep them. A personal drive has no Mine / Everyone
 * choice — everything there is the person's own.
 */
export function RecentView({ urlPrefix, isOrg, orgEmail, tenantName }: {
  urlPrefix: string
  isOrg?: boolean
  orgEmail?: string
  tenantName?: string
}) {
  const { t } = useTranslation('files')
  const [params, setParams] = useSearchParams()
  const scope: RecentWorkScope = isOrg && params.get('scope') === 'mine' ? 'mine' : 'everyone'
  const rawFilter = params.get('filter')
  const filter: RecentWorkFilter = FILTERS.includes(rawFilter as RecentWorkFilter) ? rawFilter as RecentWorkFilter : 'all'
  const setQuery = useCallback((next: { scope?: RecentWorkScope; filter?: RecentWorkFilter }) => {
    const s = next.scope ?? scope
    const f = next.filter ?? filter
    const q: Record<string, string> = {}
    if (s === 'mine') q.scope = 'mine'
    if (f !== 'all') q.filter = f
    setParams(q, { replace: true })
  }, [scope, filter, setParams])

  // The Drive deck stays mounted behind `display: none`: while it is hidden the query is paused (only
  // marked stale by file events), and reloads when the deck is shown again.
  const shown = useScreenShown()
  const recent = useRecentWork(scope, filter, PAGE_SIZE, { subscribed: shown })
  const words = useRecentWords(orgEmail, tenantName)
  const nav = useRecentNav(urlPrefix)
  const pages = recent.data?.pages ?? []
  // Each group once: a cursor that no longer opens (the server's key rotated) restarts at page 1.
  // `lastAddsNone`: the last page added nothing new (empty, or only groups already shown).
  const { groups, lastAddsNone } = useMemo(() => {
    const seen = new Set<string>()
    const all = recent.data?.pages ?? []
    let added = 0
    const out = all.flatMap((p, i) => p.groups.filter((g) => {
      if (seen.has(g.id)) return false
      seen.add(g.id)
      if (i === all.length - 1) added++
      return true
    }))
    return { groups: out, lastAddsNone: all.length > 0 && added === 0 }
  }, [recent.data])
  const sections = useMemo(() => groupByDay(groups), [groups])
  // A page can be empty while older changes exist (all it scanned was hidden): read on, a few pages.
  // Counted by ATTEMPTS (a failed one adds no page), stopped by an error, reset with the query, and
  // never cancelling a refetch already running.
  const lastEmpty = lastAddsNone
  const autoTries = useRef({ key: '', n: 0 })
  // The whole query key (scope, filter, size, identity): a switch to another one starts counting again.
  const queryId = JSON.stringify(fileKeys.recentWork('paged', scope, filter, PAGE_SIZE))
  if (autoTries.current.key !== queryId) autoTries.current = { key: queryId, n: 0 }
  useEffect(() => {
    if (!shown || !lastEmpty || !recent.hasNextPage || recent.isFetching || recent.isFetchNextPageError) return
    if (autoTries.current.n >= AUTO_CONTINUE_PAGES) return
    autoTries.current.n += 1
    void recent.fetchNextPage({ cancelRefetch: false })
  }, [queryId, shown, lastEmpty, recent.hasNextPage, recent.isFetching, recent.isFetchNextPageError, pages.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const scrollRef = useRef<HTMLDivElement>(null)
  useSwapScroll('drive-recent', scrollRef)

  // "Nothing changed" only once there is nothing more to read.
  const empty = !recent.isLoading && !recent.isError && groups.length === 0 && !recent.hasNextPage
  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto w-full px-8 py-8">
        <div className="flex items-center gap-2 mb-1">
          <Clock size={18} className="text-indigo-400" />
          <h1 className="text-xl font-semibold themed-text">{t('driveHome.recent.title')}</h1>
        </div>
        <p className="text-sm themed-text-muted mb-5">{t('driveHome.recent.subtitle')}</p>

        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap mb-7">
          {isOrg && (
            <div role="group" aria-label={t('driveHome.recent.scope.label')} className="flex items-center gap-1.5" data-testid="recent-scope">
              {(['everyone', 'mine'] as const).map((s) => (
                <button key={s} type="button" aria-pressed={scope === s} onClick={() => setQuery({ scope: s })}
                  className="ws-filter-chip" data-testid={`recent-scope-${s}`}>
                  {t(`driveHome.recent.scope.${s}`)}
                </button>
              ))}
            </div>
          )}
          <div role="group" aria-label={t('driveHome.recent.filter.label')} className="flex items-center gap-1.5 flex-wrap" data-testid="recent-filter">
            {FILTERS.map((f) => (
              <button key={f} type="button" aria-pressed={filter === f} onClick={() => setQuery({ filter: f })}
                className="ws-filter-chip" data-testid={`recent-filter-${f}`}>
                {t(`driveHome.recent.filter.${f}`)}
              </button>
            ))}
          </div>
        </div>

        {recent.isLoading ? (
          <div className="text-sm themed-text-muted px-1 py-2">{t('driveHome.loading')}</div>
        ) : recent.isError && groups.length === 0 ? (
          <div className="text-sm themed-text-muted px-1 py-2 flex items-center gap-2" role="alert">
            {t('driveHome.recent.loadError')}
            <button type="button" onClick={() => void recent.refetch()} className="text-indigo-400 hover:text-indigo-300">{t('driveHome.recent.retry')}</button>
          </div>
        ) : empty ? (
          <div className="text-sm themed-text-muted px-1 py-2" data-testid="recent-empty">
            {filter !== 'all'
              ? t('driveHome.recent.empty.filtered')
              : scope === 'mine'
                ? <>
                    {t('driveHome.recent.empty.mine')}{' '}
                    <button type="button" onClick={() => setQuery({ scope: 'everyone' })} className="text-indigo-400 hover:text-indigo-300">
                      {t('driveHome.recent.empty.seeEveryone')}
                    </button>
                  </>
                : t('driveHome.recent.empty.everyone')}
          </div>
        ) : (
          <>
            {sections.map(({ section, groups: inSection }) => (
              <section key={section} className="mb-7" data-testid={`recent-section-${section}`}>
                <h2 className="ws-home-heading mb-3">{t(`driveHome.recent.sections.${section}`)}</h2>
                <RecentGroupList groups={inSection} words={words} nav={nav} />
              </section>
            ))}
            {recent.hasNextPage && (
              <div className="flex justify-center">
                {/* Disabled while ANY fetch runs: a click would cancel a refresh in flight. */}
                <button type="button" onClick={() => void recent.fetchNextPage({ cancelRefetch: false })} disabled={recent.isFetching}
                  className="themed-btn-secondary rounded-lg px-4 py-2 text-sm disabled:opacity-60" data-testid="recent-show-more">
                  {recent.isFetching ? t('driveHome.loading') : t('driveHome.recent.showMore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
